// FieldVision Admin - Order Detail Component v5
// Merged: tidslinje + email tracking (v4) + pris-redigering (claude-work)
import React, { useState, useEffect } from 'react';
import {
  getOrder,
  sendCustomerConfirmation,
  assignPilot,
  getPilotsNearest,
  updateOrderPrice,
  resendPilotEmail,
  OrderDetail as OrderDetailType,
  Pilot,
} from '../services/api';
import MarkUploadSection from './MarkUploadSection';

// Status konfiguration
const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  Modtaget: { label: 'Modtaget', color: '#ca8a04', bgColor: '#fef9c3' },
  Bekræftet: { label: 'Bekræftet', color: '#16a34a', bgColor: '#dcfce7' },
  Behandles: { label: 'Behandles', color: '#2563eb', bgColor: '#dbeafe' },
  Udføres: { label: 'Udføres', color: '#9333ea', bgColor: '#f3e8ff' },
  Fløjet: { label: 'Fløjet', color: '#0891b2', bgColor: '#cffafe' },
  Færdig: { label: 'Færdig', color: '#6b7280', bgColor: '#f3f4f6' },
};

// Drone setup baseret på ordre type
const DRONE_SETUP: Record<string, string> = {
  NDVI: 'GSD 8cm / 120m / 65%',
  Ukrudt: 'GSD 1cm / 35m / 65%',
};

interface OrderDetailProps {
  orderId: number;
  onBack: () => void;
}

const OrderDetailComponent: React.FC<OrderDetailProps> = ({ orderId, onBack }) => {
  const [data, setData] = useState<OrderDetailType | null>(null);
  const [pilots, setPilots] = useState<Pilot[]>([]);
  const [selectedPilotId, setSelectedPilotId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [pilotNote, setPilotNote] = useState('');

  // Pris-redigering states (v5)
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [newPrice, setNewPrice] = useState<number>(0);
  const [discountReason, setDiscountReason] = useState('');

  const loadOrder = async () => {
    setLoading(true);
    setError('');
    try {
      const orderData = await getOrder(orderId);
      setData(orderData);

      // Hvis status er Bekræftet, hent piloter
      if (orderData.order.status === 'Bekræftet' && orderData.fields.length > 0) {
        const firstField = orderData.fields[0];
        if (firstField.geometry?.coordinates) {
          // Beregn centrum af første mark
          const coords = firstField.geometry.coordinates[0];
          let sumLat = 0, sumLon = 0;
          coords.forEach((c: number[]) => {
            sumLon += c[0];
            sumLat += c[1];
          });
          const centerLat = sumLat / coords.length;
          const centerLon = sumLon / coords.length;

          const pilotData = await getPilotsNearest(centerLat, centerLon);
          setPilots(pilotData.pilots);
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrder();
  }, [orderId]);

  // Sæt initial pris når data er hentet (v5)
  useEffect(() => {
    if (data?.order) {
      setNewPrice(Math.round(data.order.totalPrice));
    }
  }, [data]);

  const handleSendCustomer = async () => {
    const isResend = data?.order.customerEmailSentAt;
    const confirmMessage = isResend
      ? 'Gensend bekræftelsesmail til kunden?'
      : 'Send bekræftelsesmail til kunden?';

    if (!window.confirm(confirmMessage)) return;

    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      await sendCustomerConfirmation(orderId);
      setSuccess(isResend ? 'Bekræftelsesmail gensendt!' : 'Bekræftelsesmail sendt!');
      loadOrder(); // Refresh
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignPilot = async () => {
    if (!selectedPilotId) {
      setError('Vælg en pilot først');
      return;
    }

    const pilot = pilots.find((p) => p.pilotId === selectedPilotId);
    if (!window.confirm(`Tildel ordre til ${pilot?.name}?`)) return;

    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      await assignPilot(orderId, selectedPilotId, pilotNote);
      setSuccess(`Ordre tildelt til ${pilot?.name}!`);
      loadOrder(); // Refresh
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Gem pris funktion (v5)
  const handleSavePrice = async () => {
    setActionLoading(true);
    setError('');
    try {
      await updateOrderPrice(orderId, newPrice, discountReason);
      setSuccess('Pris opdateret!');
      setShowPriceModal(false);
      loadOrder();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Gensend pilot-mail handler (v8)
  const handleResendPilotEmail = async () => {
    if (!window.confirm('Gensend opgave-mail til piloten?')) return;

    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const result = await resendPilotEmail(orderId);
      setSuccess(result.message);
      loadOrder();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const getGoogleMapsUrl = (geometry: any) => {
    if (!geometry?.coordinates?.[0]) return null;
    const coords = geometry.coordinates[0];
    let sumLat = 0, sumLon = 0;
    coords.forEach((c: number[]) => {
      sumLon += c[0];
      sumLat += c[1];
    });
    const lat = sumLat / coords.length;
    const lon = sumLon / coords.length;
    return `https://www.google.com/maps?q=${lat},${lon}`;
  };

  const getGeojsonIoUrl = (geometry: any, marknr: string, areal: number): string | null => {
    if (!geometry?.coordinates?.[0]) return null;
    try {
      const geojson = {
        type: "Feature",
        properties: { name: "Mark " + marknr, areal: areal + " ha" },
        geometry: { type: "Polygon", coordinates: geometry.coordinates }
      };
      return "https://geojson.io/#data=data:application/json," + encodeURIComponent(JSON.stringify(geojson));
    } catch (e) {
      return null;
    }
  };

  const formatDateTime = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('da-DK', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Render tidslinje (v4)
  const renderTimeline = () => {
    if (!data) return null;
    const { order } = data;

    // Byg timeline events som simple objekter
    const events: Array<{ icon: string; label: string; date: string; pending: boolean }> = [];

    // Ordre oprettet
    events.push({
      icon: '✅',
      label: 'Ordre oprettet',
      date: formatDateTime(order.createdAt),
      pending: false,
    });

    // Email sendt til kunde
    if (order.customerEmailSentAt) {
      events.push({
        icon: '📧',
        label: 'Email sendt til kunde',
        date: formatDateTime(order.customerEmailSentAt),
        pending: false,
      });
    }

    // Kunde bekræftet
    if (order.customerConfirmedAt) {
      events.push({
        icon: '✅',
        label: 'Kunde bekræftet',
        date: formatDateTime(order.customerConfirmedAt),
        pending: false,
      });
    } else if (order.customerEmailSentAt) {
      events.push({
        icon: '⏳',
        label: 'Afventer kunde bekræftelse',
        date: '',
        pending: true,
      });
    }

    // Pilot tildelt
    if (order.pilotEmailSentAt) {
      events.push({
        icon: '👨‍✈️',
        label: order.pilotName ? `Pilot tildelt (${order.pilotName})` : 'Pilot tildelt',
        date: formatDateTime(order.pilotEmailSentAt),
        pending: false,
      });
    }

    // Pilot accepteret
    if (order.pilotConfirmedAt) {
      events.push({
        icon: '🚁',
        label: 'Pilot accepteret',
        date: formatDateTime(order.pilotConfirmedAt),
        pending: false,
      });
    } else if (order.pilotEmailSentAt && !order.completedAt) {
      events.push({
        icon: '⏳',
        label: 'Afventer pilot accept',
        date: '',
        pending: true,
      });
    }

    // Pilot har fløjet (status Fløjet eller Færdig)
    if (order.status === 'Fløjet' || order.status === 'Færdig') {
      events.push({
        icon: '🛩️',
        label: 'Pilot har fløjet',
        date: '',
        pending: false,
      });
    }

    // Færdig
    if (order.completedAt) {
      events.push({
        icon: '🏁',
        label: 'Opgave færdig',
        date: formatDateTime(order.completedAt),
        pending: false,
      });
    }

    return (
      <div style={styles.timeline}>
        {events.map((event, index) => (
          <div
            key={index}
            style={{
              ...styles.timelineItem,
              opacity: event.pending ? 0.6 : 1,
            }}
          >
            <span style={styles.timelineIcon}>{event.icon}</span>
            <span style={styles.timelineLabel}>{event.label}</span>
            <span style={styles.timelineDate}>{event.date}</span>
          </div>
        ))}
      </div>
    );
  };

  // Render primær handling med dynamisk knap tekst (v4)
  const renderPrimaryAction = () => {
    if (!data) return null;
    const { order } = data;

    // Status: Modtaget
    if (order.status === 'Modtaget') {
      // Kunde har allerede bekræftet
      if (order.customerConfirmedAt) {
        return (
          <div style={styles.confirmedBox}>
            <span style={{ fontSize: '24px' }}>✅</span>
            <span>Kunde bekræftet {formatDateTime(order.customerConfirmedAt)}</span>
          </div>
        );
      }

      // Email er sendt, men ikke bekræftet - vis gensend knap
      if (order.customerEmailSentAt) {
        return (
          <div style={styles.pilotSelect}>
            <div style={styles.waitingInfo}>
              <span>📧 Email sendt {formatDateTime(order.customerEmailSentAt)}</span>
              <span style={styles.waitingSubtext}>Afventer kunde bekræftelse</span>
            </div>
            <button
              onClick={handleSendCustomer}
              disabled={actionLoading}
              style={styles.secondaryButton}
            >
              {actionLoading ? 'Sender...' : '📧 Gensend bekræftelse'}
            </button>
          </div>
        );
      }

      // Ingen email sendt endnu
      return (
        <button
          onClick={handleSendCustomer}
          disabled={actionLoading}
          style={styles.primaryButton}
        >
          {actionLoading ? 'Sender...' : '📧 Send bekræftelse til kunde'}
        </button>
      );
    }

    // Status: Bekræftet
    if (order.status === 'Bekræftet') {
      return (
        <div style={styles.pilotSelect}>
          <label style={styles.selectLabel}>Vælg pilot:</label>
          <select
            value={selectedPilotId || ''}
            onChange={(e) => setSelectedPilotId(Number(e.target.value))}
            style={styles.select}
          >
            <option value="">-- Vælg pilot --</option>
            {pilots.map((pilot) => (
              <option key={pilot.pilotId} value={pilot.pilotId}>
                {pilot.name} ({pilot.city})
                {pilot.distance !== null && pilot.distance !== undefined && ` - ${pilot.distance} km`}
              </option>
            ))}
          </select>
          <div style={{ width: '100%', maxWidth: '500px' }}>
            <label style={styles.selectLabel}>Bemærkning til pilot (valgfri):</label>
            <textarea
              value={pilotNote}
              onChange={(e) => setPilotNote(e.target.value)}
              placeholder="F.eks. 'Skal flyves senest 15. juni' eller 'Ring til landmand inden flyvning'"
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '12px',
                fontSize: '14px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>
          <button
            onClick={handleAssignPilot}
            disabled={actionLoading || !selectedPilotId}
            style={{
              ...styles.primaryButton,
              opacity: !selectedPilotId ? 0.5 : 1,
            }}
          >
            {actionLoading ? 'Tildeler...' : '👨‍✈️ Send opgave til pilot'}
          </button>
        </div>
      );
    }

    // Status: Behandles
    if (order.status === 'Behandles') {
      return (
        <div style={styles.waitingBox}>
          <span style={styles.waitingIcon}>⏳</span>
          <span>Afventer pilot bekræftelse...</span>
          {order.pilotName && (
            <span style={styles.pilotInfo}>Pilot: {order.pilotName}</span>
          )}
          <button
            onClick={handleResendPilotEmail}
            disabled={actionLoading}
            style={{...styles.secondaryButton, marginTop: '12px'}}
          >
            {actionLoading ? 'Sender...' : '📧 Gensend pilot-mail'}
          </button>
        </div>
      );
    }

    // Status: Udføres
    if (order.status === 'Udføres') {
      return (
        <div style={styles.waitingBox}>
          <span style={styles.waitingIcon}>🚁</span>
          <span>Pilot udfører opgave - afventer færdigmelding...</span>
          {order.pilotName && (
            <span style={styles.pilotInfo}>Pilot: {order.pilotName}</span>
          )}
          <button
            onClick={handleResendPilotEmail}
            disabled={actionLoading}
            style={{...styles.secondaryButton, marginTop: '12px'}}
          >
            {actionLoading ? 'Sender...' : '📧 Gensend pilot-mail'}
          </button>
        </div>
      );
    }

    // Status: Fløjet
    if (order.status === 'Fløjet') {
      return (
        <div style={styles.waitingBox}>
          <span style={styles.waitingIcon}>📤</span>
          <span>Pilot har fløjet - afventer kort-upload</span>
          {order.pilotName && (
            <span style={styles.pilotInfo}>Pilot: {order.pilotName}</span>
          )}
          <button
            onClick={handleResendPilotEmail}
            disabled={actionLoading}
            style={{...styles.secondaryButton, marginTop: '12px'}}
          >
            {actionLoading ? 'Sender...' : '📧 Gensend pilot-mail'}
          </button>
        </div>
      );
    }

    // Status: Færdig
    if (order.status === 'Færdig') {
      return (
        <div style={styles.completeBox}>
          <span style={styles.completeIcon}>✅</span>
          <span>Ordre afsluttet {order.completedAt ? formatDateTime(order.completedAt) : ''}</span>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Henter ordre...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>Kunne ikke hente ordre</div>
        <button onClick={onBack} style={styles.backButton}>← Tilbage</button>
      </div>
    );
  }

  const { order, fields, history } = data;
  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.Færdig;
  const droneSetup = DRONE_SETUP[order.mapType] || 'Standard';

  // Beregn priser for visning (v5)
  const calculatedPrice = (order as any).calculatedPrice || order.totalPrice;
  const discountAmount = (order as any).discountAmount || 0;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={onBack} style={styles.backButton}>← Tilbage</button>
        <div style={styles.headerCenter}>
          <h2 style={styles.title}>Ordre #{order.orderId}</h2>
          <span style={styles.orderNumber}>{order.orderNumber}</span>
        </div>
        <span
          style={{
            ...styles.statusBadge,
            color: statusConfig.color,
            backgroundColor: statusConfig.bgColor,
          }}
        >
          {statusConfig.label}
        </span>
      </div>

      {/* Alerts */}
      {error && <div style={styles.alert}>{error}</div>}
      {success && <div style={styles.successAlert}>{success}</div>}

      {/* Content Grid */}
      <div style={styles.grid}>
        {/* Kunde Info */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Kunde</h3>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Navn</span>
            <span style={styles.infoValue}>{order.customerName || 'Ikke angivet'}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Email</span>
            <span style={styles.infoValue}>{order.customerEmail}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Telefon</span>
            <span style={styles.infoValue}>{order.customerPhone || '-'}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>CVR</span>
            <span style={styles.infoValue}>{order.customerCVR || '-'}</span>
          </div>
          {order.customerNotes && (
            <div style={{marginTop: '16px', padding: '12px', backgroundColor: '#fef9c3', borderRadius: '8px', border: '1px solid #fde047'}}>
              <div style={{fontSize: '12px', color: '#854d0e', fontWeight: 600, marginBottom: '4px'}}>Kundens bemærkning</div>
              <div style={{color: '#713f12', fontStyle: 'italic'}}>{order.customerNotes}</div>
            </div>
          )}
        </div>

        {/* Ordre Info */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Bestilling</h3>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Type</span>
            <span style={styles.infoValue}>{order.mapType} Droneflyvning</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Marker</span>
            <span style={styles.infoValue}>{fields.length} stk</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Areal</span>
            <span style={styles.infoValue}>{order.totalArea?.toFixed(2)} ha</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Drone</span>
            <span style={styles.infoValue}>{droneSetup}</span>
          </div>
          {order.pilotName && (
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Pilot</span>
              <span style={styles.infoValue}>{order.pilotName}</span>
            </div>
          )}

          {/* Pris sektion - v5 */}
          <div style={{
            marginTop: '16px',
            padding: '16px',
            backgroundColor: '#f0fdf4',
            borderRadius: '8px',
            border: '1px solid #bbf7d0'
          }}>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', fontWeight: 600 }}>
              Pris
            </div>

            {/* Original pris (altid synlig) */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: '#6b7280' }}>Beregnet pris:</span>
              <span>{Math.round(calculatedPrice).toLocaleString('da-DK')} kr</span>
            </div>

            {/* Rabat linje (kun hvis rabat er givet) */}
            {discountAmount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#dc2626' }}>
                <span>Rabat:</span>
                <span>-{Math.round(discountAmount).toLocaleString('da-DK')} kr</span>
              </div>
            )}

            {/* Endelig pris */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingTop: '8px',
              borderTop: '1px solid #16a34a',
              marginTop: '8px'
            }}>
              <span style={{ fontWeight: 600, color: '#16a34a' }}>Endelig pris:</span>
              <span style={{ fontWeight: 600, color: '#16a34a', fontSize: '18px' }}>
                {Math.round(order.totalPrice).toLocaleString('da-DK')} kr
              </span>
            </div>

            {/* Rediger knap - kun for Modtaget status */}
            {order.status === 'Modtaget' && (
              <button
                onClick={() => setShowPriceModal(true)}
                style={{
                  marginTop: '12px',
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                Rediger pris / Giv rabat
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Marker */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Marker ({fields.length})</h3>
        <div style={styles.fieldsList}>
          {fields.map((field) => {
            const polygonUrl = field.geometry ? getGeojsonIoUrl(field.geometry, field.marknr, field.areal) : null;
            const mapsUrl = field.geometry ? getGoogleMapsUrl(field.geometry) : null;

            return (
              <div key={field.id} style={styles.fieldItem}>
                <div style={styles.fieldInfo}>
                  <span style={styles.fieldName}>Mark {field.marknr || 'Ukendt'}</span>
                  <span style={styles.fieldArea}>{field.areal?.toFixed(2)} ha</span>
                  {field.cvr && <span style={{...styles.fieldArea, color: '#6b7280', fontSize: '11px', marginLeft: '8px'}}>| CVR: {field.cvr}</span>}
                </div>
                <div style={styles.fieldLinks}>
                  {polygonUrl && (
                    <a
                      href={polygonUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.polygonLink}
                    >
                      Se polygon
                    </a>
                  )}
                  {mapsUrl && (
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.mapsLink}
                    >
                      Google Maps
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tidslinje (v4) */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Tidslinje</h3>
        {renderTimeline()}
      </div>

      {/* Upload Analysekort */}
      <div style={styles.section}>
        <MarkUploadSection
          orderId={order.orderId}
          orderStatus={order.status}
          fields={fields.map((f) => ({
            Id: f.id,
            Marknr: f.marknr,
            CVR: f.cvr,
            Areal: f.areal,
          }))}
        />
      </div>

      {/* Primær Handling */}
      <div style={styles.actionSection}>
        {renderPrimaryAction()}
      </div>

      {/* Historik */}
      <div style={styles.section}>
        <button
          onClick={() => setHistoryExpanded(!historyExpanded)}
          style={styles.historyToggle}
        >
          {historyExpanded ? '▼' : '▶'} Historik ({history.length})
        </button>
        {historyExpanded && (
          <div style={styles.historyList}>
            {history.map((h) => (
              <div key={h.historyId} style={styles.historyItem}>
                <span style={styles.historyDate}>{formatDateTime(h.createdAt)}</span>
                <span style={styles.historyText}>
                  {h.note || `${h.oldStatus} → ${h.newStatus}`}
                </span>
                <span style={styles.historyBy}>{h.changedBy}</span>
              </div>
            ))}
            <div style={styles.historyItem}>
              <span style={styles.historyDate}>{formatDateTime(order.createdAt)}</span>
              <span style={styles.historyText}>Ordre oprettet</span>
              <span style={styles.historyBy}>System</span>
            </div>
          </div>
        )}
      </div>

      {/* Pris-redigerings modal (v5) */}
      {showPriceModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px' }}>Rediger pris</h3>

            {/* Original pris info */}
            <div style={{
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>Beregnet pris</div>
              <div style={{ fontSize: '20px', fontWeight: 600 }}>
                {Math.round(calculatedPrice).toLocaleString('da-DK')} kr
              </div>
              <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                {order.totalArea?.toFixed(2)} ha x {order.pricePerHa} kr/ha + 699 kr opstart
              </div>
            </div>

            {/* Ny pris input */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>
                Ny pris (kr)
              </label>
              <input
                type="number"
                value={newPrice}
                onChange={(e) => setNewPrice(Number(e.target.value))}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '18px',
                  border: '2px solid #d1d5db',
                  borderRadius: '8px',
                  boxSizing: 'border-box'
                }}
              />
              {newPrice < calculatedPrice && (
                <div style={{ marginTop: '8px', color: '#16a34a', fontSize: '14px' }}>
                  Rabat: {(calculatedPrice - newPrice).toLocaleString('da-DK')} kr
                </div>
              )}
            </div>

            {/* Rabat årsag (valgfri) */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>
                Årsag til rabat (valgfri)
              </label>
              <select
                value={discountReason}
                onChange={(e) => setDiscountReason(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px'
                }}
              >
                <option value="">Vælg årsag...</option>
                <option value="100+ ha">Mængderabat (100+ ha)</option>
                <option value="Returning customer">Tilbagevendende kunde</option>
                <option value="Campaign">Kampagne</option>
                <option value="Other">Andet</option>
              </select>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowPriceModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                Annuller
              </button>
              <button
                onClick={handleSavePrice}
                disabled={actionLoading}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#16a34a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  opacity: actionLoading ? 0.5 : 1
                }}
              >
                {actionLoading ? 'Gemmer...' : 'Gem pris'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    maxWidth: '900px',
    margin: '0 auto',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#6b7280',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
    gap: '16px',
  },
  backButton: {
    padding: '8px 16px',
    fontSize: '14px',
    color: '#6b7280',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  headerCenter: {
    flex: 1,
    textAlign: 'center',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 600,
  },
  orderNumber: {
    fontSize: '12px',
    color: '#9ca3af',
  },
  statusBadge: {
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 600,
  },
  alert: {
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
    marginBottom: '16px',
  },
  successAlert: {
    padding: '12px 16px',
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '8px',
    color: '#16a34a',
    marginBottom: '16px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  cardTitle: {
    margin: '0 0 16px 0',
    fontSize: '14px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #f3f4f6',
  },
  infoLabel: {
    color: '#6b7280',
    fontSize: '14px',
  },
  infoValue: {
    color: '#1f2937',
    fontSize: '14px',
    fontWeight: 500,
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    margin: '0 0 12px 0',
    fontSize: '14px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
  },
  fieldsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  fieldItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: 'white',
    padding: '12px 16px',
    borderRadius: '8px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  fieldThumbnail: {
    width: '48px',
    height: '48px',
    backgroundColor: '#f3f4f6',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fieldIcon: {
    fontSize: '24px',
  },
  fieldInfo: {
    flex: 1,
  },
  fieldName: {
    display: 'block',
    fontWeight: 500,
    color: '#1f2937',
  },
  fieldArea: {
    fontSize: '13px',
    color: '#6b7280',
  },
  mapsLink: {
    fontSize: '13px',
    color: '#2563eb',
    textDecoration: 'none',
  },
  fieldLinks: {
    display: 'flex',
    gap: '12px',
  },
  polygonLink: {
    fontSize: '13px',
    color: '#16a34a',
    textDecoration: 'none',
  },
  // Timeline styles (v4)
  timeline: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  timelineItem: {
    display: 'grid',
    gridTemplateColumns: '32px 1fr auto',
    gap: '12px',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid #f3f4f6',
  },
  timelineIcon: {
    fontSize: '18px',
    textAlign: 'center',
  },
  timelineLabel: {
    color: '#1f2937',
    fontSize: '14px',
  },
  timelineDate: {
    color: '#6b7280',
    fontSize: '13px',
    textAlign: 'right',
  },
  // Action section styles
  actionSection: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    marginBottom: '24px',
    textAlign: 'center',
  },
  primaryButton: {
    padding: '16px 32px',
    fontSize: '16px',
    fontWeight: 600,
    color: 'white',
    backgroundColor: '#16a34a',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#6b7280',
    backgroundColor: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  pilotSelect: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'center',
  },
  selectLabel: {
    fontSize: '14px',
    color: '#6b7280',
  },
  select: {
    padding: '12px 16px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    minWidth: '300px',
  },
  waitingBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '20px',
    backgroundColor: '#f3f4f6',
    borderRadius: '8px',
    color: '#6b7280',
  },
  waitingIcon: {
    fontSize: '24px',
  },
  waitingInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '8px',
  },
  waitingSubtext: {
    fontSize: '13px',
    color: '#9ca3af',
  },
  pilotInfo: {
    fontSize: '13px',
    color: '#374151',
    fontWeight: 500,
  },
  completeBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '20px',
    backgroundColor: '#f0fdf4',
    borderRadius: '8px',
    color: '#16a34a',
  },
  completeIcon: {
    fontSize: '24px',
  },
  confirmedBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '20px',
    backgroundColor: '#f0fdf4',
    borderRadius: '8px',
    color: '#16a34a',
  },
  historyToggle: {
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#6b7280',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
  },
  historyList: {
    marginTop: '8px',
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
  },
  historyItem: {
    display: 'grid',
    gridTemplateColumns: '120px 1fr 150px',
    padding: '12px 16px',
    borderBottom: '1px solid #f3f4f6',
    fontSize: '13px',
  },
  historyDate: {
    color: '#9ca3af',
  },
  historyText: {
    color: '#1f2937',
  },
  historyBy: {
    color: '#6b7280',
    textAlign: 'right',
  },
  error: {
    padding: '16px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
  },
};

export default OrderDetailComponent;
